import React, { useMemo } from 'react';

export interface ParallaxStarsProps {
  speed?: number;
  className?: string;
  density?: 'low' | 'normal' | 'high';
  opacity?: number;
}

// Deterministic or memoized random box-shadow generator
const generateBoxShadows = (count: number, width = 2000, height = 2000) => {
  const shadows: string[] = [];
  for (let i = 0; i < count; i++) {
    const x = Math.floor(Math.random() * width);
    const y = Math.floor(Math.random() * height);
    const alpha = (0.4 + Math.random() * 0.6).toFixed(2);
    shadows.push(`${x}px ${y}px rgba(255, 255, 255, ${alpha})`);
  }
  return shadows.join(', ');
};

export const ParallaxStars: React.FC<ParallaxStarsProps> = ({
  speed = 1,
  className = "",
  density = 'normal',
  opacity = 0.8
}) => {
  const multiplier = density === 'high' ? 1.5 : density === 'low' ? 0.6 : 1;

  const shadowsSmall = useMemo(() => generateBoxShadows(Math.floor(450 * multiplier)), [multiplier]);
  const shadowsMedium = useMemo(() => generateBoxShadows(Math.floor(140 * multiplier)), [multiplier]);
  const shadowsBig = useMemo(() => generateBoxShadows(Math.floor(60 * multiplier)), [multiplier]);

  return (
    <div 
      className={`absolute inset-0 z-0 overflow-hidden pointer-events-none ${className}`}
      style={{ opacity }}
    >
      <style>{`
        @keyframes animStarField {
          from { transform: translateY(0px); }
          to { transform: translateY(-2000px); }
        }
      `}</style>

      {/* Deep Space Radial Atmosphere */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#0b1d2e_0%,_#030910_70%,_#020509_100%)] z-0" />

      {/* Layer 1: Small Star Particles (Fastest) */}
      <div 
        className="absolute left-0 top-0 w-[1px] h-[1px] bg-transparent z-[1]"
        style={{ 
          boxShadow: shadowsSmall,
          animation: `animStarField ${45 / speed}s linear infinite`
        }}
      >
        <div 
          className="absolute top-[2000px] w-[1px] h-[1px] bg-transparent"
          style={{ boxShadow: shadowsSmall }}
        />
      </div>

      {/* Layer 2: Medium Star Particles (Medium Speed) */}
      <div 
        className="absolute left-0 top-0 w-[2px] h-[2px] bg-transparent z-[1]"
        style={{ 
          boxShadow: shadowsMedium,
          animation: `animStarField ${90 / speed}s linear infinite`
        }}
      >
        <div 
          className="absolute top-[2000px] w-[2px] h-[2px] bg-transparent"
          style={{ boxShadow: shadowsMedium }}
        />
      </div>

      {/* Layer 3: Big Star Particles with Glow (Slowest & Deepest) */}
      <div 
        className="absolute left-0 top-0 w-[3px] h-[3px] bg-transparent z-[1]"
        style={{ 
          boxShadow: shadowsBig,
          animation: `animStarField ${140 / speed}s linear infinite`
        }}
      >
        <div 
          className="absolute top-[2000px] w-[3px] h-[3px] bg-transparent"
          style={{ boxShadow: shadowsBig }}
        />
      </div>

      {/* Soft Vignette Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50 z-[2]" />
    </div>
  );
};

export default ParallaxStars;
